import { useRef, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import Styles from './NewFileModal.module.scss'
import { AlmanacFile, AlmanacFileSummary, BaseEmoji } from 'types'
import { EmojiPicker } from 'components/AlmanacFileTitle/EmojiPicker'
import { FileTitle } from 'components/FilenameAndBreadcrumb/FilenameAndBreadcrumb'
import { ProjectCard } from 'components/BlazeCommandLine/components/ProjectCard'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateDoc } from 'components/NewEntrypointModal/hooks/useCreateDoc'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCloseLeftSidebar } from 'hooks/useCloseLeftSidebar'
import { Heading } from 'almanac-ui/Heading/Heading'
import { generatePath, useHistory } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { useCreateDesign } from 'hooks/queries/workspaces/useCreateDesign'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useLocationPickerProps } from 'components/LocationPicker/hooks/useLocationPicker'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { InlineLocationPicker } from 'components/InlineLocationPicker/InlineLocationPicker'
import { useDefaultFolder } from 'hooks/queries/projects/useDefaultFolder'
import { DesignFormatSelector } from './DesignFormatSelector'
import { Document, Design } from 'blaze-ui/icons'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { ASPECT_RATIOS, AspectRatioKey, DesignChannels } from './Types'

type FileType = 'document' | 'design'

interface Props {
  project?: AlmanacFile | null
  onSuccess?: (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> }) => void
  initialFileType?: FileType
  initialAspectRatioKey?: AspectRatioKey
}

export const NewFileModal = ({
  project,
  onSuccess,
  initialFileType = 'document',
  initialAspectRatioKey = 'instagram-portrait-post',
}: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const { closeAllModals, closeModal, stack } = useModals()
  const { isDesktop, isMobileMd } = useResponsiveDesign()
  const [title, setTitle] = useState<string>('')
  const [fileType, setFileType] = useState<FileType>(initialFileType)
  const [emoji, setEmoji] = useState<BaseEmoji | null>(null)
  const { closeLeftSidebar } = useCloseLeftSidebar()
  const inputRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const { currentFolder } = useCurrentFolderStore()
  const defaultFolder = useDefaultFolder()
  const [chosenFolder, setChosenFolder] = useState(project || currentFolder || defaultFolder)
  let destinationFolder = chosenFolder || currentFolder || defaultFolder

  if (destinationFolder?.uploads_project) {
    destinationFolder = defaultFolder
  }

  const locationProps = useLocationPickerProps({
    folder: destinationFolder,
    onSelectedFolder: setChosenFolder,
  })

  const [aspectRatioKey, setAspectRatioKey] = useState<AspectRatioKey>(initialAspectRatioKey)
  const designerEnabled = useFlipperFlag('visual_studio')

  const { createDoc, isLoading } = useCreateDoc({
    chosenWorkspaceId: workspaceId,
    chosenFolder: { project_id: destinationFolder?.project_id, handbook: destinationFolder?.handbook },
    docOrFolderName: title,
    emoji: emoji?.native,
    onSuccess: async (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> }) => {
      onSuccess?.(data)
      closeLeftSidebar()
      closeAllModals()
      history.push(generatePath(DOC_PATH, { slug: data.almanac_file.id }))
    },
  })

  const designGenerationMutation = useCreateDesign(
    { workspaceId: workspaceId },
    {
      onSuccess: (res: any) => {
        onSuccess?.({ almanac_file: res.almanac_file })
        closeAllModals()
        history.push(generatePath(DESIGN_PATH, { key: res.almanac_file.id }))
      },
      onError: () => {
        showToastMessage('Something went wrong.', 'error')
      },
    },
  )

  const createDesign = async () => {
    const aspectRatio = ASPECT_RATIOS[aspectRatioKey]
    designGenerationMutation.mutate({
      almanac_file: {
        title,
        parent_id: destinationFolder?.id,
        header_emoji: emoji?.native,
      },
      width: aspectRatio.width,
      height: aspectRatio.height,
      channel: DesignChannels[aspectRatioKey],
    })
  }

  const handleCreate = async () => {
    if (fileType === 'document') {
      createDoc()
    } else {
      createDesign()
    }
  }

  const isCreating = isLoading || designGenerationMutation.isLoading

  return (
    <Modal.Root size={isDesktop ? 'lg' : 'sm'}>
      <Modal.Header title="New File" />

      <Modal.Content className={Styles.modalContent}>
        <div className={Styles.content}>
          <div className={Styles.contentInner}>
            <Heading level={4}>Name your creation</Heading>
            <div className={Styles.titleWrapper}>
              <EmojiPicker emoji={emoji?.native || ''} onSelect={setEmoji} size="lg" />
              <div ref={inputRef} className={Styles.titleInput}>
                <Input
                  autoFocus={!isMobileMd}
                  aria-label="Title"
                  name="title"
                  size="lg"
                  placeholder="Untitled"
                  onChange={(event) => setTitle(event.currentTarget.value)}
                  value={title === 'Untitled' ? '' : title}
                />
              </div>
              <div className={Styles.locationPickerButton}>
                <InlineLocationPicker
                  {...locationProps}
                  variant="tertiary"
                  size="lg"
                  withChevron="down"
                  dropdownTriggerProps={{ restoreFocus: false }}
                />
              </div>
            </div>
            <Heading level={4}>Pick the type</Heading>
            <div className={Styles.cards}>
              <ProjectCard
                title="Document"
                description="Blog post, brainstorm, article, copy ideas, email etc. "
                icon={Document}
                color="var(--teal-50)"
                isSelected={fileType === 'document'}
                onClick={() => setFileType('document')}
                dataTestId="document-card"
              />
              {designerEnabled && (
                <ProjectCard
                  title="Design"
                  description="Social media post, landing page, etc."
                  icon={Design}
                  color="var(--green-50)"
                  isSelected={fileType === 'design'}
                  onClick={() => setFileType('design')}
                  dataTestId="design-card"
                />
              )}
            </div>

            {fileType === 'design' && <DesignFormatSelector value={aspectRatioKey} onChange={setAspectRatioKey} />}
          </div>
          {isDesktop && (
            <div className={Styles.previewContainer}>
              {fileType === 'document' && (
                <div className={Styles.previewTitleContainer}>
                  <FileTitle
                    title={title || 'Untitled'}
                    emoji={emoji?.native}
                    color={!title ? 'var(--neutral-30)' : undefined}
                  />
                </div>
              )}
              <Preview type={fileType} />
            </div>
          )}
        </div>
      </Modal.Content>

      <Modal.Footer className={Styles.footer}>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} size="lg" variant="tertiary" data-testid="back-btn">
            {stack.length > 1 ? 'Back' : 'Cancel'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton size="lg" variant="primary" onPress={handleCreate} isDisabled={isCreating || !title}>
            {fileType === 'document' ? 'Create Document' : 'Create Design'}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

const Preview = ({ type }: { type: FileType }) => {
  switch (type) {
    case 'design':
      return (
        <img
          src="https://res.cloudinary.com/almanac/image/upload/v1708965151/new_project_modal/o2by7690qmeasgxizg8z.png"
          alt="Design view"
        />
      )
    case 'document':
    default:
      return (
        <img
          src="https://res.cloudinary.com/almanac/image/upload/v1708965151/new_project_modal/znsmf8h0izwdofqr98qd.png"
          alt="Document view"
        />
      )
  }
}
